import BinDTO from "src/models/BinDTO";
import { BaseChartProps, BinSenseCableChart, OPICableChart, TemperatureCableBinSenseChart, TemperatureCableChart, TemperatureCableOpiChart, useBinSensePerSensorHistoryQueryOptions, useOpiPerSensorHistoryQueryOptions } from "./TemperatureCableChart";
import React, { useCallback, useMemo, useState } from "react";
import { RedoOutlined } from "@ant-design/icons";
import { useIsFetching, useQuery } from "@tanstack/react-query";
import { Space, Button, SelectProps, Select, Switch, Divider } from "antd";
import dayjs from "dayjs";
import { binDBKeys } from "src/pages/binOverview/BinCommander";
import { RangeValue } from "./ChartContainer";
import { ChartDatePicker } from "./Co2Chart";
import { ExtraCharts } from "./DryingDashboard";
import { LayerMCHistoryChart, useLayerMcHistoryQueryOptions } from "./LayerMCHistoryChart";
import { LayerTemperatureHistoryChart, useTemperatureLayerSummaryChartHistoryQueryOptions } from "./LayerTemperatureHistoryChart";
import TemperatureSensorEnum from "src/consts/TemperatureSensorEnum";
import { useBinDTOContext } from "src/queries/BinDTOContext";
import RhSensorEnum from "src/consts/RhSensorEnum";
import { BaseChartQueryOptions, BaseCsv, useAddDefaultAnnotationsToCsv } from "./modehistoryAndFanChangeHistory";
import { CSVLink } from "react-csv";
import { chartCsvHeaders, timestampHeader } from "./csv/core";
import { useGrainTypeCsv } from "./csv/useGrainTypeCsv";
import { BaseHeaders } from "./csv/types";
import { CsvButton } from "./csv/CsvButton";

enum Charts {
  LayerTemperature,
  LayerTemperatureOPI,
  LayerMCHistory,

  PerSensorTemperatureCable,
  PerSensorTemperatureOPICable,
  PerSensorTemperatureBinSenseCable,

  PerSensorMcOPICable,
  PerSensorMcBinSenseCable,

}

interface ChartsToShowDivision {
  sensors: Array<Charts>,
  summaries: Array<Charts>,
}

interface ChartToShowOptions {
  chartsToShow: ChartsToShowDivision,
}
export const MoistureCablesCsv = (props: BaseChartQueryOptions & ChartToShowOptions) => {

  const showLayerMcChart = props.chartsToShow.summaries.includes(Charts.LayerMCHistory);
  const showLayerTemperatureChart = props.chartsToShow.summaries.includes(Charts.LayerTemperature);
  const showPerSensor = props.chartsToShow.sensors.length > 0;
  const showPerSensorOPI = props.chartsToShow.sensors.some((elem) => [Charts.PerSensorTemperatureOPICable, Charts.PerSensorMcOPICable].includes(elem));
  const showPerSensorOPITemp = props.chartsToShow.sensors.includes(Charts.PerSensorTemperatureOPICable);
  const bindtoContext = useBinDTOContext();
  const showPerSensorBinSense = props.chartsToShow.sensors.some((elem) => [Charts.PerSensorTemperatureBinSenseCable, Charts.PerSensorMcBinSenseCable].includes(elem));
  const showAnyPerSensor = showPerSensorOPI || showPerSensorBinSense;


  const layerSummaryQuery = useQuery({
    ...useTemperatureLayerSummaryChartHistoryQueryOptions({
      binId: props.binId,
      range: props.range,
    }),
    enabled: !showPerSensor,
  });

  const opiPerSensorQuery = useQuery({
    ...useOpiPerSensorHistoryQueryOptions({ binId: props.binId, range: props.range }),
    enabled: showPerSensorOPI,
  });

  const binSensePerSensorQuery = useQuery({
    ...useBinSensePerSensorHistoryQueryOptions({ binId: props.binId, range: props.range}),
    enabled: showPerSensorBinSense,
  })

  const useRHForFanControl = bindtoContext.binDTO?.useRHForFanControl!;

  function flattenToCsv() {

    function selectPerSensorDataSource() {
      if (showPerSensorOPI) {
        return opiPerSensorQuery;
      }
      else {
        return binSensePerSensorQuery;
      }
    }

    const headers: BaseHeaders = [timestampHeader];
    let additionalHeaders: string[] = [];
    function addLayerHeaders( ) {
      layerSummaryQuery.data?.forEach(layer => {

        if (showLayerTemperatureChart || showLayerMcChart) {
          additionalHeaders.push(chartCsvHeaders.layerTempF(layer.label));
        }
  
        if (showLayerTemperatureChart || showLayerMcChart) {
          additionalHeaders.push(chartCsvHeaders.layerRH(layer.label));
          if (useRHForFanControl === false) {
            additionalHeaders.push(chartCsvHeaders.layerMC(layer.label));
          }
          additionalHeaders.push(chartCsvHeaders.layerAh(layer.label));
          additionalHeaders.push(chartCsvHeaders.layerDP(layer.label));
        }
  
      });
    }

    function addPerSensorHeaders() {
      const perSensorDataToUse = selectPerSensorDataSource();
      perSensorDataToUse.data?.forEach(sensor => {

        if (showAnyPerSensor) {
          additionalHeaders.push(chartCsvHeaders.sensorTempF(sensor.label));
        }
  
        if (showAnyPerSensor) {
          additionalHeaders.push(chartCsvHeaders.sensorRH(sensor.label));
          if (useRHForFanControl === false) {
            additionalHeaders.push(chartCsvHeaders.layerMC(sensor.label));
          }
          additionalHeaders.push(chartCsvHeaders.sensorAh(sensor.label));
          additionalHeaders.push(chartCsvHeaders.sensorDP(sensor.label));
        }
  
      });
    }

    if (props.chartsToShow.summaries.length) {
      addLayerHeaders();
    }
    else {
      addPerSensorHeaders();
    }




    headers.push(...additionalHeaders);


    const data: BaseCsv[] = [];

    const getTimeSeries = () => {
      if (showPerSensorOPI) {
        return opiPerSensorQuery.data?.[0]?.data?.map(row => row.x) ?? [];
      }
      else if (showPerSensorBinSense) {
        return binSensePerSensorQuery.data?.[0]?.data?.map(row => row.x) ?? [];
      }
      return layerSummaryQuery.data?.[0]?.data?.map(row => row.x) ?? [];
    }

    const timeSeries = getTimeSeries();

    function fillLayerData() {
    timeSeries.forEach((timestamp, index) => {
      const newRow: BaseCsv = { [timestampHeader]: timestamp };
      layerSummaryQuery.data?.forEach(layer => {
        const tempHeader = chartCsvHeaders.layerTempF(layer.label);
        // not a mistake. the temperature used to calculate the mc is exposed to the customer
        // Regardless of the preferred temperature source (ex. thermocouples)
        newRow[tempHeader] = layer.data?.[index]?.moistureCableTemperatureF;

        const mcHeader = chartCsvHeaders.layerMC(layer.label);
        const dewpointHeader = chartCsvHeaders.layerDP(layer.label);
        const rhHeader = chartCsvHeaders.layerRH(layer.label);
        const ahHeader = chartCsvHeaders.layerAh(layer.label);
        newRow[mcHeader] = layer.data?.[index]?.mc;
        newRow[dewpointHeader] = layer.data?.[index]?.dewPointF;
        newRow[rhHeader] = layer.data?.[index]?.relativeHumidity;
        newRow[ahHeader] = layer.data?.[index]?.ah;

      });
      data.push(newRow);
    });
  }


    function fillPerSensorData() {
      timeSeries.forEach((timestamp, index) => {
        const newRow: BaseCsv = { [timestampHeader]: timestamp };
        const perSensorQueryToUse = selectPerSensorDataSource();
        perSensorQueryToUse.data?.forEach(sensor => {
          if (sensor.data?.[index]?.x !== timestamp) {
            // this may happen if new cables were added
            // for now they are skipped
              return;
          }
          // this is intended. we give the customer the hidden temperature data
          if (showPerSensor) {
            const tempHeader = chartCsvHeaders.sensorTempF(sensor.label);
            newRow[tempHeader] = sensor.data?.[index]?.temperatureF;
          }

          if (showPerSensor) {
            const mcHeader = chartCsvHeaders.sensorMC(sensor.label);
            const dewpointHeader = chartCsvHeaders.sensorDP(sensor.label);
            const rhHeader = chartCsvHeaders.sensorRH(sensor.label);
            const ahHeader = chartCsvHeaders.sensorAh(sensor.label!);
            newRow[mcHeader] = sensor.data?.[index]?.y;
            newRow[dewpointHeader] = sensor.data?.[index]?.dewPointF;
            newRow[rhHeader] = sensor.data?.[index]?.rh;
            newRow[ahHeader] = sensor.data?.[index]?.ah;
          };


        });

        data.push(newRow);
      });
    }

  if (!showPerSensor) {
    fillLayerData();
  } else {
    fillPerSensorData();
  }

    return {
      headers: headers, data: data
    };
  }

  const data = flattenToCsv();

  const withAnnotions = useAddDefaultAnnotationsToCsv({
    baseData: data.data,
    baseHeaders: data.headers,
    binId: props.binId, range: props.range,
  });

  const finalCsv = useGrainTypeCsv({...withAnnotions});

  return <CsvButton data={finalCsv.data} headers={finalCsv.headers} baseFileName="moisture-cables" />
}

export const MoistureCableChartTab = (props: BaseChartProps & { preferredTemperatureSource: TemperatureSensorEnum }) => {

  const binDTOContext = useBinDTOContext();
  const preferredSensorType = binDTOContext?.binDTO?.rhSensorsType;
  const useRHForFanControl = binDTOContext?.binDTO?.useRHForFanControl!;
  const currentDate = dayjs();
  const priorDate = currentDate.subtract(14, 'days');


  const isControlled = typeof props.value != 'undefined';
  const [internalValue, setIntervalValue] = useState<RangeValue>([priorDate, currentDate]);
  const inprogress = useIsFetching([...binDBKeys.bin(props.binId), "charts"]);

  // Internally, we need to deal with some value. Depending on whether
  // the component is controlled or not, that value comes from its
  // props or from its internal state.

  const value = isControlled ? props.value : internalValue;

  const onDateChange = useCallback((values: RangeValue, formatter?: any) => {
    if (props.onChange) {
      props.onChange(values);
    }
    else {
      setIntervalValue(values);
    }
  }, [props.onChange]);



  const [showPerSensorChart, setShowPerSensorChart] = useState(false);

  const chartDivisions: ChartsToShowDivision = { sensors: [], summaries: [] };

  const showPerSensorOpiChart = showPerSensorChart && preferredSensorType === RhSensorEnum.Opi;
  const showPerSensorBinSenseChart = showPerSensorChart && [RhSensorEnum.BinSense, RhSensorEnum.PowerCast].includes(preferredSensorType!);


  if (!showPerSensorChart) {
    chartDivisions.summaries.push(Charts.LayerMCHistory);
  }

  if (!showPerSensorChart && (useRHForFanControl === true || [TemperatureSensorEnum.Opi, TemperatureSensorEnum.PowerCast, TemperatureSensorEnum.BinSense].includes(props.preferredTemperatureSource))) {
    chartDivisions.summaries.push(Charts.LayerTemperature);
  }

  if (showPerSensorOpiChart) {
    chartDivisions.sensors.push(Charts.PerSensorMcOPICable);
    if (useRHForFanControl === true || [TemperatureSensorEnum.Opi, TemperatureSensorEnum.PowerCast].includes(props.preferredTemperatureSource)) {
      chartDivisions.sensors.push(Charts.PerSensorTemperatureOPICable);
    }
  }

  if (showPerSensorBinSenseChart) {
    chartDivisions.sensors.push(Charts.PerSensorMcBinSenseCable);
    if (useRHForFanControl === true || [TemperatureSensorEnum.BinSense, TemperatureSensorEnum.PowerCast].includes(props.preferredTemperatureSource)) {
      chartDivisions.sensors.push(Charts.PerSensorTemperatureBinSenseCable);
    }
  }

  const onlyOneChart = (): boolean =>{
    if (useRHForFanControl === true) {
      return false;
    }
    if ([TemperatureSensorEnum.Thermocouple].includes(props.preferredTemperatureSource)) {
      return true;
    }
    return false;

  }



  return <>
    <div>
      <Space direction="horizontal" wrap>
        <ChartDatePicker startDate={value?.[0]!} endDate={value?.[1]!} onDateRangeChange={onDateChange} />
        <Button type="primary" disabled={inprogress > 0} icon={<RedoOutlined spin={inprogress > 0} />} onClick={() => {
          onDateChange([value?.[0]!, dayjs()], ['', '']);
        }}>
        </Button>

        <Switch unCheckedChildren="By Layer" checkedChildren="By Sensor" checked={showPerSensorChart} onChange={(checked, event) => {
          setShowPerSensorChart(checked);
        }} />

        <MoistureCablesCsv binId={props.binId} range={value} chartsToShow={chartDivisions} />

      </Space>

      <div style={{ width: "100%" }}>

        {chartDivisions.summaries.includes(Charts.LayerMCHistory) && <LayerMCHistoryChart binId={props.binId} deviceId={props.deviceId} useRHForFanControl={binDTOContext.binDTO?.useRHForFanControl!} onChange={onDateChange} value={value} chartHeight={onlyOneChart() ? "60vh" : "30vh"} />}
        {chartDivisions.summaries.includes(Charts.LayerTemperature) && <>
          <LayerTemperatureHistoryChart binId={props.binId} deviceId={props.deviceId} onChange={onDateChange} useRHForFanControl={binDTOContext.binDTO?.useRHForFanControl!} value={value} chartHeight={onlyOneChart() ? "60vh" : "30vh"} preferredSensorType={props.preferredTemperatureSource} />
        </>
        }
        {chartDivisions.sensors.includes(Charts.PerSensorMcOPICable) && <>
          <OPICableChart value={value} binId={props.binId} deviceId={props.deviceId} useRHForFanControl={useRHForFanControl} chartHeight={onlyOneChart() ? "60vh" : "30vh"} />

          {/* Todo: BinSense graph support */}
          {chartDivisions.sensors.includes(Charts.PerSensorTemperatureOPICable) && <>
            <TemperatureCableOpiChart binId={props.binId} value={value} deviceId={props.deviceId} useRHForFanControl={useRHForFanControl} chartHeight={onlyOneChart() ? "60vh" : "30vh"} />
          </>
          }
        </>}

        {chartDivisions.sensors.includes(Charts.PerSensorMcBinSenseCable) && <>
          <BinSenseCableChart value={value} binId={props.binId} deviceId={props.deviceId} useRHForFanControl={useRHForFanControl} chartHeight={onlyOneChart() ? "60vh" : "30vh"} />

          {/* Todo: BinSense graph support */}
          {chartDivisions.sensors.includes(Charts.PerSensorTemperatureBinSenseCable) && <>
            <TemperatureCableBinSenseChart binId={props.binId} value={value} deviceId={props.deviceId} useRHForFanControl={useRHForFanControl} chartHeight={onlyOneChart() ? "60vh" : "30vh"} />
          </>
          }
        </>}

      </div>
    </div>
  </>

}